import { downloadApiFile, req } from "./transport";
import type { ProjectAgreement, AgreementRevisionInput, AgreementDocument } from "./types";
import type { HireOfferDetails, HireOfferInput } from "./types";
import type { JobOfferTerms } from "./types/employment";

export type ContractChecklistItem = { id: string; label: string; done: boolean; doneAt: string | null; createdAt: string;
  createdByRole: string; createdByUserId: number | null; doneByUserId: number | null };
export type ContractChecklistAction = { id: string; itemId: string; actorUserId: number; actorName: string | null; actorCompanyId: string | null;
  actorRole: string; action: "add" | "done" | "undone"; label: string; createdAt: string };
export type ContractWork = { items: ContractChecklistItem[]; history: ContractChecklistAction[]; canWrite: boolean; readOnlyReason: string | null };
export type ChecklistCommand = { commandId: string } & ({ action: "add"; label: string } | { action: "set"; itemId: string; done: boolean });
export type Engagement = { id: string; applicationId: string | null; jobId: string; jobTitle: string;
  employerUserId: number; employerCompanyId: string | null; employerName: string; workerUserId: number; workerName: string;
  counterpartName: string; role: "employer" | "worker"; agreedSalary: number | null; status: "active" | "completed" | "cancelled";
  paymentRequired?: boolean; paymentContractId?: string | null; paymentStatus?: string | null;
  createdAt: string; updatedAt: string; completedAt: string | null };
export type EngagementWork = ContractWork & { engagement: Engagement; threadId: string | null; jobOfferId: string | null; proposalTerms?: JobOfferTerms | null };
export type EngagementSummary = Engagement & { tasksTotal: number; tasksDone: number };

export const contractsApi = {
  engagementWork(id: string) { return req<EngagementWork>(`/engagements/${encodeURIComponent(id)}/work`, {}, { rejectUnauthorized: true }); },
  engagementChecklistCommand(id: string, command: ChecklistCommand) {
    return req<{ item: ContractChecklistItem; replayed: boolean }>(`/engagements/${encodeURIComponent(id)}/work/checklist/commands`, { method: "POST", body: JSON.stringify(command) }, { rejectUnauthorized: true });
  },
  contractWork(id: string) { return req<ContractWork>(`/contracts/${encodeURIComponent(id)}/work`, {}, { rejectUnauthorized: true }); },
  checklistCommand(id: string, command: ChecklistCommand) {
    return req<{ item: ContractChecklistItem; replayed: boolean }>(`/contracts/${encodeURIComponent(id)}/work/checklist/commands`, { method: "POST", body: JSON.stringify(command) }, { rejectUnauthorized: true });
  },
  // v3.9.60 (Stage 4 / F4) — e-signature on a contract
  /** [contracts.current-proposal](../../../../docs/product/contracts.md#contracts-current-proposal): accepted project proposals require their reviewed proposal ID;
   * legacy contracts may omit it. Stale/missing IDs return 409 and require review. */
  async signContract(id: string, name: string, expectedAgreementId?: string | null, expectedDocumentId?: string) {
    return req<{ ok: boolean; buyerSignedAt: string | null; providerSignedAt: string | null; bothSigned: boolean }>(`/contracts/${encodeURIComponent(id)}/sign`, { method: "POST", body: JSON.stringify({ name, expectedAgreementId: expectedAgreementId || undefined, expectedDocumentId }) });
  },

  // v3.9.19 — post-hire work monitoring (engagements)
  async engagements(signal?: AbortSignal) {
    const r = await req<{ engagements: EngagementSummary[] }>("/engagements", { signal }, { rejectUnauthorized: true });
    if (!Array.isArray(r?.engagements)) throw new Error("Could not load Job engagements.");
    return r.engagements;
  },

  async engagement(id: string) {
    return req<{ engagement: any; tasks: any[] }>(`/engagements/${encodeURIComponent(id)}`);
  },

  async addEngagementTask(id: string, title: string) {
    return req<{ task: any }>(`/engagements/${encodeURIComponent(id)}/tasks`, { method: "POST", body: JSON.stringify({ title }) });
  },

  async toggleEngagementTask(id: string, taskId: string) {
    return req<{ task: any }>(`/engagements/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}/toggle`, { method: "POST" });
  },

  async deleteEngagementTask(id: string, taskId: string) {
    return req<{ ok: boolean }>(`/engagements/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`, { method: "DELETE" });
  },

  async setEngagementStatus(id: string, status: "active" | "completed" | "cancelled") {
    return req<{ engagement: Engagement }>(`/engagements/${encodeURIComponent(id)}/status`, { method: "POST", body: JSON.stringify({ status }) }, { rejectUnauthorized: true });
  },

  // Increment 9: contracts + escrow
  // Coordinated overview reads opt into cancellation and explicit access failure;
  // no-argument callers retain the existing unauthorized-response behavior.
  async listContracts(signal?: AbortSignal) {
    return req<{ contracts: any[] }>("/contracts", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  // v3.9.27 — direct hire: create a contract offer to a worker
  async hireWorker(input: HireOfferInput) {
    return req<{ contractId: string }>("/contracts/hire", { method: "POST", body: JSON.stringify(input) });
  },
  async hireOfferRequirements() { return req<{ fields: string[] }>("/contracts/hire/requirements"); },
  async previewHireOffer(input: HireOfferInput) {
    return req<{ document: AgreementDocument | null }>("/contracts/hire/preview", { method: "POST", body: JSON.stringify(input) });
  },
  async previewBookService(serviceId: string, details: HireOfferDetails) {
    return req<{ document: AgreementDocument | null }>("/contracts/book-service/preview", { method: "POST", body: JSON.stringify({ serviceId, ...details }) });
  },

  // QA #10 — instant Book/Buy a fixed-price service (creates an escrow contract)
  async bookService(serviceId: string, details?: HireOfferDetails) {
    return req<{ contractId: string }>("/contracts/book-service", { method: "POST", body: JSON.stringify({ serviceId, ...details }) });
  },

  async walletSummary(signal?: AbortSignal) {
    return req<{ inEscrow: number; releasedByMe: number; earned: number; incoming: number; asBuyer: number; asProvider: number }>("/contracts/summary", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async downloadContractPdf(id: string, kind: "contract" | "receipt") {
    return downloadApiFile(`/contracts/${encodeURIComponent(id)}/${kind}.pdf`, () => `${kind}-${id}.pdf`, "Could not generate the PDF.");
  },

  // Increment 32: contract checklist & check-ins
  async contractChecklist(id: string) {
    const r = await req<{ items: any[] }>(`/contracts/${encodeURIComponent(id)}/checklist`);
    return r?.items ?? [];
  },

  async addChecklistItem(id: string, label: string) {
    return req<{ item: any }>(`/contracts/${encodeURIComponent(id)}/checklist`, { method: "POST", body: JSON.stringify({ label }) });
  },

  async toggleChecklistItem(id: string, itemId: string) {
    return req<{ item: any }>(`/contracts/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}/toggle`, { method: "POST" });
  },

  async deleteChecklistItem(id: string, itemId: string) {
    return req<{ ok: boolean }>(`/contracts/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}`, { method: "DELETE" });
  },

  async contractCheckins(id: string) {
    const r = await req<{ checkins: any[] }>(`/contracts/${encodeURIComponent(id)}/checkins`);
    return r?.checkins ?? [];
  },

  async addCheckin(id: string, note: string) {
    return req<{ checkin: any }>(`/contracts/${encodeURIComponent(id)}/checkins`, { method: "POST", body: JSON.stringify({ note }) });
  },

  // Increment 31: contract amendments & cancellations
  async contractEvents(id: string) {
    const r = await req<{ events: any[] }>(`/contracts/${encodeURIComponent(id)}/events`);
    return r?.events ?? [];
  },

  async projectAgreement(id: string) {
    return req<{ agreement: ProjectAgreement; history: ProjectAgreement[] }>(`/project-agreements/${encodeURIComponent(id)}`);
  },

  async counterproposeProjectAgreement(id: string, expectedProposalId: string, input: AgreementRevisionInput) {
    return req<{ agreement: ProjectAgreement }>(`/project-agreements/${encodeURIComponent(id)}/proposals`, { method: "POST", body: JSON.stringify({ ...input, expectedProposalId }) });
  },
  async previewProjectRevision(id: string, expectedProposalId: string, input: AgreementRevisionInput) {
    return req<{ document: AgreementDocument | null }>(`/project-agreements/${encodeURIComponent(id)}/preview`, { method: "POST", body: JSON.stringify({ ...input, expectedProposalId }) });
  },
  async previewProjectNegotiation(id: string, input: AgreementRevisionInput) {
    return req<{ document: AgreementDocument | null }>(`/project-agreements/contracts/${encodeURIComponent(id)}/preview`, { method: "POST", body: JSON.stringify(input) });
  },

  async negotiateProjectContract(id: string, input: AgreementRevisionInput) {
    return req<{ agreement: ProjectAgreement }>(`/project-agreements/contracts/${encodeURIComponent(id)}/proposals`, { method: "POST", body: JSON.stringify(input) });
  },

  async acceptProjectAgreement(id: string, expectedProposalId: string, name?: string, expectedDocumentId?: string) {
    return req<{ contractId: string; agreement: ProjectAgreement }>(`/project-agreements/${encodeURIComponent(id)}/accept`, { method: "POST", body: JSON.stringify({ expectedProposalId, expectedDocumentId, ...(name ? { name } : {}) }) });
  },

  async endProjectAgreement(id: string, expectedProposalId: string, decision: "withdraw" | "decline") {
    return req<{ agreement: ProjectAgreement }>(`/project-agreements/${encodeURIComponent(id)}/${decision}`, { method: "POST", body: JSON.stringify({ expectedProposalId }) });
  },

  async proposeAmendment(id: string, body: { newTitle?: string; newAmount?: number; milestones?: Array<{ id: string; amount: number }>; message?: string }) {
    return req<{ event: any }>(`/contracts/${encodeURIComponent(id)}/amendments`, { method: "POST", body: JSON.stringify(body) });
  },

  async respondAmendment(id: string, eid: string, decision: "accept" | "reject") {
    return req<{ ok: boolean; status: string }>(`/contracts/${encodeURIComponent(id)}/amendments/${encodeURIComponent(eid)}/${decision}`, { method: "POST" });
  },

  async requestContractCancel(id: string, message?: string) {
    return req<{ ok: boolean; status: string; event: any }>(`/contracts/${encodeURIComponent(id)}/cancel`, { method: "POST", body: JSON.stringify({ message }) });
  },

  async respondContractCancel(id: string, eid: string, decision: "accept" | "reject") {
    return req<{ ok: boolean; status: string }>(`/contracts/${encodeURIComponent(id)}/cancel/${encodeURIComponent(eid)}/${decision}`, { method: "POST" });
  },

  async fundContract(id: string) {
    return req<{ contract: { id: string; status: string } }>(`/contracts/${encodeURIComponent(id)}/fund`, { method: "POST" });
  },

  // v3.9.62 (Stage 5 / G1) — escrow ledger
  async contractLedger(id: string) {
    return req<{ entries: { id: string; groupId: string; kind: string; account: string; amount: number; note: string | null; createdAt: string }[] }>(`/contracts/${encodeURIComponent(id)}/ledger`);
  },

  // v3.9.61 (Stage 5 / A1) — escrow + protection-fee cost preview before funding
  async fundingPreview(id: string) {
    return req<{ enabled: boolean; total: number; protectionFee: number; grandTotal: number; breakdown: { label: string; base: number; pct: number; amount: number }[]; clamped: "min" | "cap" | null; min: number; cap: number }>(`/contracts/${encodeURIComponent(id)}/funding-preview`);
  },

  async releaseMilestone(id: string, mid: string) {
    return req<{ ok: boolean; released: number; contractCompleted: boolean }>(`/contracts/${encodeURIComponent(id)}/milestones/${encodeURIComponent(mid)}/release`, { method: "POST" });
  },

  // Batch item 5: contract offer handshake
  async acceptOffer(id: string, signatureName?: string, expectedDocumentId?: string) {
    return req<{ ok: boolean; offerStatus: string; providerSignedAt?: string }>(`/contracts/${encodeURIComponent(id)}/offer/accept`, {
      method: "POST",
      ...((signatureName || expectedDocumentId) ? { body: JSON.stringify({ ...(signatureName ? { name: signatureName } : {}), expectedDocumentId }) } : {}),
    });
  },

  async declineOffer(id: string, expectedProposalId?: string) {
    return req<{ ok: boolean; offerStatus: string; status: string }>(`/contracts/${encodeURIComponent(id)}/offer/decline`, { method: "POST", body: JSON.stringify(expectedProposalId ? { expectedProposalId } : {}) });
  },

  async withdrawOffer(id: string) {
    return req<{ ok: boolean; offerStatus: string; status: string }>(`/contracts/${encodeURIComponent(id)}/offer/withdraw`, { method: "POST" });
  },

  // Batch item 6: time tracking
  async contractTimeLogs(id: string) {
    return req<{ logs: any[]; totalMinutes: number; openLogId: string | null }>(`/contracts/${encodeURIComponent(id)}/time-logs`);
  },

  async contractTimeIn(id: string, note?: string) {
    return req<{ log: any }>(`/contracts/${encodeURIComponent(id)}/timein`, { method: "POST", body: JSON.stringify({ note }) });
  },

  async contractTimeOut(id: string, note?: string) {
    return req<{ log: any }>(`/contracts/${encodeURIComponent(id)}/timeout`, { method: "POST", body: JSON.stringify({ note }) });
  },

  // Batch item 7: progress log
  async contractProgress(id: string) {
    const r = await req<{ entries: any[] }>(`/contracts/${encodeURIComponent(id)}/progress`);
    return r?.entries ?? [];
  },

  async addContractProgress(id: string, note: string, logType?: "update" | "milestone" | "issue") {
    return req<{ entry: any }>(`/contracts/${encodeURIComponent(id)}/progress`, { method: "POST", body: JSON.stringify({ note, logType }) });
  }
};
